import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { NeedsChecklist } from "@/pages/admin/NeedsChecklist";
import { Loader2, Search, FileText, Download, Send, Plus, DollarSign, X, Archive, Mail, Award, Bot, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { statusColor, FINANCING_TYPES, DOC_CATEGORIES } from "@/data/site";
import { LenderMatch } from "@/components/site/LenderMatch";
import { useAuth } from "@/context/AuthContext";
import { EditDealDialog, StaffUpload } from "@/pages/admin/AdminDealTools";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABEL = { loan: "Loan Applications", emd: "EMD Requests", transactional: "Transactional Funding" };
const STATUS_FLOWS = {
  loan: ["Submitted", "Under Review", "Additional Information Needed", "Terms Available", "Approved", "Closing", "Funded", "Declined"],
  emd: ["Submitted", "Under Review", "Approved", "Documents Pending", "Funding Scheduled", "Funded", "Repaid", "Closed", "Declined"],
  transactional: ["Submitted", "Under Review", "Transaction Verified", "Approved", "Funding Scheduled", "A-B Closed", "B-C Closed", "Repaid", "Closed", "Declined"],
};

function Badge({ status }) { return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColor(status)}`}>{status}</span>; }
function Loading() { return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>; }

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/submissions").then((r) => setRecent(r.data.slice(0, 8))).catch(() => {});
  }, []);
  if (!stats) return <Loading />;
  const cards = [
    { k: "Loan Applications", v: stats.loan.total, sub: `${stats.loan.new} new` },
    { k: "EMD Requests", v: stats.emd.total, sub: `${stats.emd.new} new` },
    { k: "Transactional", v: stats.transactional.total, sub: `${stats.transactional.new} new` },
    { k: "Funded (all)", v: stats.loan.funded + stats.emd.funded + stats.transactional.funded, sub: "total" },
    { k: "Borrowers", v: stats.borrowers, sub: "registered" },
    { k: "Referrals", v: stats.referrals, sub: "applications" },
    { k: "Inquiries", v: stats.contacts, sub: "contact form" },
    { k: "In Review", v: stats.loan.review + stats.emd.review + stats.transactional.review, sub: "under review" },
  ].filter((c) => c.v !== undefined);
  return (
    <div data-testid="admin-dashboard">
      <h1 className="h-display text-3xl text-white">Dashboard</h1>
      <p className="mt-1 text-dim">Operations overview.</p>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.k} className="card-surface rounded-xl p-5" data-testid={`admin-stat-${c.k.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="eyebrow">{c.k}</div>
            <div className="font-mono text-3xl text-white mt-1">{c.v}</div>
            <div className="text-xs text-faint mt-1">{c.sub}</div>
          </div>
        ))}
      </div>
      <h2 className="h-display text-xl text-white mt-10 mb-3">Recent Submissions</h2>
      <div className="space-y-2">
        {recent.map((s) => (
          <div key={s.id} className="card-surface rounded-xl px-4 py-3 flex items-center justify-between">
            <div><span className="font-mono text-gold text-sm">{s.reference}</span><span className="text-white ml-3">{s.applicant_name || s.email}</span><span className="text-faint text-xs ml-2">· {s.property_label}</span></div>
            <Badge status={s.status} />
          </div>
        ))}
        {recent.length === 0 && <p className="text-faint">No submissions yet.</p>}
      </div>
    </div>
  );
}

export function AdminSubmissions({ type }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [active, setActive] = useState(null);

  const load = () => { api.get("/admin/submissions", { params: { type } }).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("open");
    if (id && rows && !active) { const hit = rows.find((x) => x.id === id); if (hit) { setActive(hit); setParams({}, { replace: true }); } }
  }, [rows, params]); // eslint-disable-line

  const filtered = useMemo(() => {
    let r = rows || [];
    if (status !== "all") r = r.filter((x) => x.status === status);
    if (q) { const ql = q.toLowerCase(); r = r.filter((x) => (x.reference + x.applicant_name + x.email + x.property_label + (x.company || "")).toLowerCase().includes(ql)); }
    return r;
  }, [rows, q, status]);

  if (rows === null) return <Loading />;

  return (
    <div data-testid={`admin-submissions-${type}`}>
      <h1 className="h-display text-3xl text-white">{TYPE_LABEL[type]}</h1>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-faint absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, name, email, property…" className="pl-9 bg-surface/70 border-edge text-white h-11" data-testid="admin-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-56 bg-surface/70 border-edge text-white h-11" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-charcoal border-edge text-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_FLOWS[type].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-faint border-b border-edge">
            <th className="py-3 px-3 font-semibold">Reference</th><th className="py-3 px-3 font-semibold">Applicant</th>
            <th className="py-3 px-3 font-semibold hidden md:table-cell">Property</th><th className="py-3 px-3 font-semibold">Status</th>
            <th className="py-3 px-3 font-semibold hidden lg:table-cell">Date</th>
          </tr></thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} onClick={() => setActive(s)} className="border-b border-edge/60 hover:bg-surface/50 cursor-pointer" data-testid={`admin-row-${s.reference}`}>
                <td className="py-3 px-3 font-mono text-gold">{s.reference}</td>
                <td className="py-3 px-3 text-white">{s.applicant_name || s.email}<div className="text-xs text-faint">{s.email}</div></td>
                <td className="py-3 px-3 text-dim hidden md:table-cell">{s.property_label}</td>
                <td className="py-3 px-3"><Badge status={s.status} /></td>
                <td className="py-3 px-3 text-faint hidden lg:table-cell">{new Date(s.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-faint py-8 text-center">No submissions found.</p>}
      </div>

      {active && <DetailModal type={type} row={active} onClose={() => setActive(null)} onChanged={() => { load(); }} setActive={setActive} />}
    </div>
  );
}

function DetailModal({ type, row, onClose, onChanged, setActive }) {
  const [s, setS] = useState(row);
  const [newStatus, setNewStatus] = useState(row.status);
  const [statusNote, setStatusNote] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [docReq, setDocReq] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [assignTo, setAssignTo] = useState(row.assigned_to || "");
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (isAdmin) api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, [isAdmin]);
  const [fee, setFee] = useState(row.internal_fee || "");
  const [lender, setLender] = useState(row.partner_lender || "");
  const [closing, setClosing] = useState(row.closing_date || "");
  const [fundedAmt, setFundedAmt] = useState(row.funded_amount || "");
  const [showPa, setShowPa] = useState(false);

  const reload = async () => { const { data } = await api.get(`/admin/submissions/${type}/${row.id}`); setS(data); };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const act = async (fn) => { try { await fn(); await reload(); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const updateStatus = () => act(async () => { await api.put(`/admin/submissions/${type}/${row.id}/status`, { status: newStatus, note: statusNote || null }); setStatusNote(""); toast.success("Status updated"); });
  const addNote = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/notes`, { note }); setNote(""); toast.success("Note added"); });
  const requestDocs = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/request-documents`, { items: docReq.split(",").map((x) => x.trim()).filter(Boolean) }); setDocReq(""); toast.success("Documents requested"); });
  const assign = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/assign`, { assigned_to: assignTo }); toast.success(assignTo ? "Assigned" : "Unassigned"); });
  const sendMsg = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/messages`, { body: msg }); setMsg(""); });
  const markFunded = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/funded`, { internal_fee: fee, partner_lender: lender, closing_date: closing, funded_amount: fundedAmt }); toast.success("Marked funded"); });
  const archive = () => act(async () => { await api.post(`/admin/submissions/${type}/${row.id}/archive`); toast.success("Archived"); onClose(); });

  const dataEntries = Object.entries(s.data || {}).filter(([, v]) => v !== "" && v != null);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-edge text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-detail-modal">
        <div className="flex items-start justify-between">
          <div>
            <span className="font-mono text-gold">{s.reference}</span>
            <h2 className="h-display text-2xl text-white mt-1">{s.applicant_name || s.email}</h2>
            <p className="text-sm text-dim">{s.email} {s.phone && `· ${s.phone}`} {s.company && `· ${s.company}`}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge status={s.status} />
            <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="btn-outline-glass rounded-lg" data-testid="admin-edit-deal">Edit Details</Button>
          </div>
        </div>
        {editing && <EditDealDialog type={type} sub={s} onClose={() => setEditing(false)} onSaved={() => { reload(); onChanged(); }} />}
        {s.source === "phone" && <p className="text-xs text-faint mt-1" data-testid="admin-phone-source">Taken by phone · {s.created_by_name}</p>}

        <div className="mt-4">
          <Button onClick={() => setShowPa((v) => !v)} className="btn-gold rounded-lg h-11 px-5 gap-2 w-full sm:w-auto" data-testid="open-preapproval-btn">
            <Award className="w-4 h-4" /> {showPa ? "Hide Pre-Approval Editor" : "Issue Pre-Approval Letter"}
          </Button>
          {showPa && <PreapprovalPanel type={type} subId={row.id} />}
        </div>

        {/* Details */}
        <Section title="Transaction Details">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <KV k="Property" v={s.property_label} />
            {dataEntries.map(([k, v]) => <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} />)}
          </div>
        </Section>

        {s.requested_lender && (
          <div className="mt-6 rounded-xl border border-gold/50 bg-gold/[0.08] p-4 flex items-center gap-2" data-testid="admin-requested-lender">
            <DollarSign className="w-5 h-5 text-gold" />
            <span className="text-white text-sm">Borrower requested: <strong className="text-gold">{s.requested_lender.name || s.requested_lender.id}</strong>
              <span className="text-faint text-xs ml-2">{s.requested_lender.at ? new Date(s.requested_lender.at).toLocaleString() : ""}</span></span>
          </div>
        )}

        {s.lender_match && <LenderMatch data={s.lender_match} variant="admin" termSheetBase={`${API}/admin/submissions/${type}/${row.id}/term-sheet`} />}

        <SendToLender type={type} subId={row.id} match={s.lender_match} requested={s.requested_lender} onSent={reload} />

        <LenderSubmitAgent type={type} subId={row.id} match={s.lender_match} requested={s.requested_lender} />

        {/* Documents */}
        <Section title={`Documents (${(s.documents || []).length})`}>
          {(s.documents || []).length ? (s.documents || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-edge bg-surface/50 px-3 py-2 text-sm mb-1.5">
              <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> {d.original_filename} <span className="text-faint text-xs">· {d.category}</span></span>
              <a href={`${API}/files/${d.id}/download`} target="_blank" rel="noreferrer" className="text-gold text-xs inline-flex items-center gap-1" data-testid={`admin-doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /> Download</a>
            </div>
          )) : <p className="text-faint text-sm">No documents uploaded.</p>}
          <StaffUpload type={type} subId={row.id} onDone={() => { reload(); onChanged(); }} />
        </Section>

        {/* Status control */}
        <Section title="Update Status">
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="bg-surface/70 border-edge text-white h-11" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-charcoal border-edge text-white">{STATUS_FLOWS[type].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Optional note" className="bg-surface/70 border-edge text-white h-11 flex-1" />
            <Button onClick={updateStatus} className="btn-gold rounded-lg h-11 px-5" data-testid="admin-update-status">Update</Button>
          </div>
        </Section>

        {/* Assign / Request docs / Funded */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Section title="Assigned To">
            {isAdmin ? (
              <div className="flex gap-2">
                <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="flex-1 h-11 rounded-md bg-surface/70 border border-edge text-white px-3 text-sm" data-testid="admin-assign-input">
                  <option value="">Unassigned</option>
                  {staff.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email} · {m.role.replace("_", " ")}</option>)}
                </select>
                <Button onClick={assign} variant="outline" className="btn-outline-glass rounded-lg h-11" data-testid="admin-assign-save">Assign</Button>
              </div>
            ) : <p className="text-sm text-white" data-testid="admin-assigned-name">{s.assigned_name || "Unassigned"}</p>}
          </Section>
          <Section title="Request Documents">
            <div className="flex gap-2">
              <Input value={docReq} onChange={(e) => setDocReq(e.target.value)} placeholder="e.g. Appraisal, Title" className="bg-surface/70 border-edge text-white h-11" data-testid="admin-request-docs-input" />
              <Button onClick={requestDocs} variant="outline" className="btn-outline-glass rounded-lg h-11" data-testid="admin-request-docs-save"><Plus className="w-4 h-4" /></Button>
            </div>
          </Section>
        </div>

        {(s.requested_documents || []).length > 0 && (
          <div className="text-xs text-dim">Requested: {(s.requested_documents || []).map((r) => `${r.item}${r.fulfilled ? " ✓" : ""}`).join(", ")}</div>
        )}

        {isAdmin && <Section title="Mark Funded (records internal detail)">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input value={fundedAmt} onChange={(e) => setFundedAmt(e.target.value)} type="number" placeholder="Funded loan amount ($)" className="bg-surface/70 border-edge text-white h-11" data-testid="admin-funded-amount" />
            <Input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Internal fee" className="bg-surface/70 border-edge text-white h-11" data-testid="admin-fee-input" />
            <Input value={lender} onChange={(e) => setLender(e.target.value)} placeholder="Partner lender" className="bg-surface/70 border-edge text-white h-11" />
            <Input value={closing} onChange={(e) => setClosing(e.target.value)} placeholder="Closing date" className="bg-surface/70 border-edge text-white h-11" />
          </div>
          <p className="text-xs text-faint mt-1.5">Funded amount drives referral-partner payouts (1.5% Bullseye fee × partner tier).</p>
          <Button onClick={markFunded} className="btn-gold rounded-lg h-11 px-5 mt-2 gap-1" data-testid="admin-mark-funded"><DollarSign className="w-4 h-4" /> Mark Funded</Button>
        </Section>}

        {/* Internal notes */}
        <Section title="Internal Notes (not visible to borrower)">
          <div className="space-y-2 mb-3">
            {(s.notes || []).map((n, i) => (
              <div key={i} className="rounded-lg bg-surface/50 border border-edge px-3 py-2 text-sm"><div className="text-xs text-faint">{n.by} · {new Date(n.at).toLocaleString()}</div>{n.note}</div>
            ))}
            {(s.notes || []).length === 0 && <p className="text-faint text-sm">No notes.</p>}
          </div>
          <div className="flex gap-2">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" rows={2} className="bg-surface/70 border-edge text-white" data-testid="admin-note-input" />
            <Button onClick={addNote} variant="outline" className="btn-outline-glass rounded-lg self-end h-11" data-testid="admin-add-note">Add</Button>
          </div>
        </Section>

        {/* Messages */}
        <Section title="Messages with Borrower">
          <div className="space-y-2 max-h-56 overflow-y-auto mb-3">
            {(s.messages || []).map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.sender_role === "staff" ? "ml-auto bg-gold/15 border border-gold/30" : "bg-surface border border-edge"}`}>
                <div className="text-xs text-faint mb-0.5">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</div>{m.body}
              </div>
            ))}
            {(s.messages || []).length === 0 && <p className="text-faint text-sm">No messages.</p>}
          </div>
          <div className="flex gap-2">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Message borrower…" className="bg-surface/70 border-edge text-white h-11" data-testid="admin-message-input" />
            <Button onClick={sendMsg} className="btn-gold rounded-lg h-11 w-11 p-0" data-testid="admin-message-send"><Send className="w-4 h-4" /></Button>
          </div>
        </Section>

        <div className="flex justify-between pt-2">
          {isAdmin ? <Button onClick={archive} variant="ghost" className="text-faint hover:text-brandred gap-1" data-testid="admin-archive"><Archive className="w-4 h-4" /> Archive</Button> : <span />}
          <Button onClick={onClose} variant="outline" className="btn-outline-glass rounded-lg"><X className="w-4 h-4 mr-1" /> Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return <div className="border-t border-edge pt-4 mt-4"><div className="eyebrow mb-2.5">{title}</div>{children}</div>;
}
function KV({ k, v }) {
  return <div className="flex flex-col"><span className="text-faint text-xs capitalize">{k}</span><span className="text-white">{v}</span></div>;
}

function SendToLender({ type, subId, match, requested, onSent }) {
  const matches = match?.matches || [];
  const [lenders, setLenders] = useState([]);
  const [lenderId, setLenderId] = useState(requested?.id || matches[0]?.lender_id || "");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const mismoUrl = `${API}/admin/submissions/${type}/${subId}/mismo`;
  const termUrl = `${API}/admin/submissions/${type}/${subId}/term-sheet?lender_id=${lenderId || "none"}`;

  useEffect(() => {
    api.get("/lenders").then((r) => setLenders((r.data || []).filter((l) => l.active !== false))).catch(() => {});
  }, []);

  const matchIds = new Set(matches.map((m) => m.lender_id));
  const others = lenders.filter((l) => !matchIds.has(l.id));

  const send = async () => {
    if (!email) { toast.error("Enter the lender's email"); return; }
    setBusy(true);
    try {
      const matched = matches.find((m) => m.lender_id === lenderId);
      const lender = lenders.find((l) => l.id === lenderId);
      const lenderName = matched?.lender_name || lender?.name || "";
      const { data } = await api.post(`/admin/submissions/${type}/${subId}/send-to-lender`, {
        to_email: email, lender_id: lenderId || null, lender_name: lenderName, message: note, include_terms: true,
      });
      toast.success(`Sending to ${data.to} with download links: combined deal PDF, cover sheet + ${data.documents} document(s). Check Internal Notes for delivery.`);
      if (onSent) { [8000, 30000, 90000].forEach((ms) => setTimeout(onSent, ms)); }
      setEmail(""); setNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Section title="Send to Lender">
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-dim mb-1 block">Lender (for indicative terms)</label>
          <select value={lenderId} onChange={(e) => setLenderId(e.target.value)} className="w-full h-11 rounded-md bg-surface/70 border border-edge text-white text-sm px-3" data-testid="send-lender-select">
            <option value="">— No terms, deal summary only —</option>
            {matches.length > 0 && (
              <optgroup label="Matched lenders">
                {matches.map((m) => <option key={m.lender_id} value={m.lender_id}>{m.lender_name} · {m.product_name}</option>)}
              </optgroup>
            )}
            {others.length > 0 && (
              <optgroup label="All other lenders (no indicative terms)">
                {others.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="text-xs text-dim mb-1 block">Lender email</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="broker@lender.com" className="bg-surface/70 border-edge text-white h-11" data-testid="send-lender-email" />
        </div>
      </div>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the lender…" className="bg-surface/70 border-edge text-white mt-2" data-testid="send-lender-note" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={send} disabled={busy} className="btn-gold rounded-lg h-11 px-5 gap-2" data-testid="send-lender-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send Deal</>}
        </Button>
        <a href={mismoUrl} target="_blank" rel="noreferrer" data-testid="download-mismo">
          <Button variant="outline" className="btn-outline-glass rounded-lg h-11 gap-2"><Download className="w-4 h-4" /> Download MISMO 3.4</Button>
        </a>
        <a href={termUrl} target="_blank" rel="noreferrer" data-testid="download-term-sheet">
          <Button variant="outline" className="btn-outline-glass rounded-lg h-11 gap-2"><FileText className="w-4 h-4" /> Term Sheet PDF</Button>
        </a>
      </div>
    </Section>
  );
}

function PaField({ label, k, f, set, textarea, rows, type, testid }) {
  return (
    <div>
      <label className="text-xs text-dim mb-1 block">{label}</label>
      {textarea ? (
        <Textarea value={f[k] || ""} onChange={set(k)} rows={rows || 3} className="bg-surface/70 border-edge text-white" data-testid={testid} />
      ) : (
        <Input value={f[k] || ""} onChange={set(k)} type={type || "text"} className="bg-surface/70 border-edge text-white h-11" data-testid={testid} />
      )}
    </div>
  );
}

function PreapprovalPanel({ type, subId }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(false);
  const letterUrl = `${API}/admin/submissions/${type}/${subId}/preapproval-letter`;

  useEffect(() => {
    api.get(`/admin/submissions/${type}/${subId}/preapproval-draft`)
      .then((r) => setF(r.data))
      .catch(() => toast.error("Could not load pre-approval draft"));
  }, [type, subId]);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const issue = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${type}/${subId}/issue-preapproval`, f);
      setIssued(true);
      toast.success("Pre-approval issued");
      window.open(letterUrl, "_blank");
    } catch (e) { toast.error("Could not issue pre-approval"); }
    finally { setBusy(false); }
  };

  if (!f) return <div className="mt-3 rounded-xl border border-edge bg-surface/40 p-5 text-faint text-sm">Loading editor…</div>;

  return (
    <div className="mt-3 rounded-xl border border-gold/40 bg-gold/[0.05] p-5 space-y-3" data-testid="preapproval-panel">
      <p className="text-xs text-faint">Every field below is editable. The letter is branded and signed as entered. Click Issue &amp; Download to save and generate the PDF.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <PaField label="Applicant" k="applicant" f={f} set={set} testid="pa-applicant" />
        <PaField label="Property" k="property_label" f={f} set={set} testid="pa-property" />
        <PaField label="Financing Type" k="financing_type" f={f} set={set} testid="pa-financing-type" />
        <PaField label="Pre-Approved Amount ($)" k="amount" f={f} set={set} type="number" testid="pa-amount" />
        <PaField label="Valid Through" k="valid_until" f={f} set={set} testid="pa-valid-until" />
        <PaField label="Greeting" k="salutation" f={f} set={set} testid="pa-salutation" />
      </div>
      <PaField label="Opening Paragraph" k="intro" f={f} set={set} textarea rows={4} testid="pa-intro" />
      <PaField label="Conditions Paragraph" k="conditions" f={f} set={set} textarea rows={5} testid="pa-conditions" />
      <PaField label="Closing Line" k="closing" f={f} set={set} testid="pa-closing" />
      <div className="grid sm:grid-cols-2 gap-3">
        <PaField label="Signatory Name" k="signatory_name" f={f} set={set} testid="pa-signatory-name" />
        <PaField label="Signatory Title" k="signatory_title" f={f} set={set} testid="pa-signatory-title" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <PaField label="Contact Phone" k="phone" f={f} set={set} testid="pa-phone" />
        <PaField label="Contact Email" k="email" f={f} set={set} testid="pa-email" />
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button onClick={issue} disabled={busy} className="btn-gold rounded-lg h-11 px-5 gap-2" data-testid="issue-preapproval-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Award className="w-4 h-4" /> Issue &amp; Download</>}
        </Button>
        {issued && (
          <a href={letterUrl} target="_blank" rel="noreferrer" data-testid="download-preapproval-admin">
            <Button variant="outline" className="btn-outline-glass rounded-lg h-11 gap-2"><Download className="w-4 h-4" /> Download Again</Button>
          </a>
        )}
      </div>
    </div>
  );
}


const SUB_STATUS = {
  submitted: { label: "Submitted", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  processing: { label: "Agent working…", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  prepared: { label: "Prepared", cls: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  portal_logged_in: { label: "Portal logged in", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  manual_required: { label: "Needs specialist", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  confirmed: { label: "Confirmed", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  created: { label: "Created", cls: "text-faint border-edge bg-surface/40" },
};

function LenderSubmitAgent({ type, subId, match, requested }) {
  const matches = match?.matches || [];
  const [lenders, setLenders] = useState([]);
  const [lenderId, setLenderId] = useState(requested?.id || matches[0]?.lender_id || "");
  const [method, setMethod] = useState("");
  const [intake, setIntake] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [confMap, setConfMap] = useState({});

  const load = async () => {
    try { const r = await api.get(`/admin/submissions/${type}/${subId}/lender-submissions`); setRows(r.data || []); } catch (e) {}
  };
  useEffect(() => {
    api.get("/lenders").then((r) => setLenders((r.data || []).filter((l) => l.active !== false))).catch(() => {});
    load();
  }, [type, subId]); // eslint-disable-line
  useEffect(() => {
    if (!rows.some((r) => r.status === "processing" || r.needs_status === "reviewing")) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [rows]); // eslint-disable-line

  const retry = async (rid) => {
    const row = rows.find((x) => x.id === rid);
    try {
      await api.post(`/admin/lender-submissions/${rid}/retry`);
      toast.success(row?.method === "email" ? "Resending email with attachments…" : "Portal robot restarted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not retry"); }
  };

  const selected = lenders.find((l) => l.id === lenderId);
  const cfgMethod = selected?.submission?.method;
  const effMethod = method || cfgMethod || "prepared";

  const submit = async () => {
    if (!lenderId) { toast.error("Choose a lender"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${type}/${subId}/submit-to-lender`, {
        lender_id: lenderId, method: method || null, intake_email: intake || null, note,
      });
      toast.success("Submission agent started");
      setNote(""); setIntake("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submission failed"); }
    finally { setBusy(false); }
  };

  const confirm = async (rid) => {
    const num = confMap[rid];
    if (!num) { toast.error("Enter the lender's confirmation number"); return; }
    try {
      await api.post(`/admin/lender-submissions/${rid}/confirm`, { confirmation_number: num });
      toast.success("Confirmation recorded");
      await load();
    } catch (e) { toast.error("Could not record confirmation"); }
  };

  const testAgent = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal-agent/test`);
      toast[r.data.ok ? "success" : "error"](`Portal agent: ${r.data.status} — ${r.data.detail}`, { duration: 8000 });
    } catch (e) { toast.error("Portal agent test failed"); }
    finally { setBusy(false); }
  };

  return (
    <Section title="Submit to Lender — BullsEye Agent">
      <p className="text-xs text-faint -mt-2 mb-3">One click assembles the full package (cover sheet + MISMO + all documents) and submits it: emails the package to email-intake lenders, or prepares & runs the portal agent for portal lenders. Confirmations go to the client and BullsEye.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-dim mb-1 block">Lender</label>
          <select value={lenderId} onChange={(e) => { setLenderId(e.target.value); setMethod(""); }} className="w-full h-11 rounded-md bg-surface/70 border border-edge text-white text-sm px-3" data-testid="agent-lender-select">
            <option value="">— Choose a lender —</option>
            {matches.length > 0 && <optgroup label="Matched lenders">{matches.map((m) => <option key={m.lender_id} value={m.lender_id}>{m.lender_name} · {m.product_name}</option>)}</optgroup>}
            {lenders.filter((l) => !matches.some((m) => m.lender_id === l.id)).length > 0 && (
              <optgroup label="All other lenders">{lenders.filter((l) => !matches.some((m) => m.lender_id === l.id)).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</optgroup>
            )}
          </select>
          {selected && <p className="text-xs text-faint mt-1">Configured method: <span className="text-dim">{cfgMethod || "not set"}</span>{selected?.submission?.portal_url ? ` · ${selected.submission.portal_url}` : ""}</p>}
        </div>
        <div>
          <label className="text-xs text-dim mb-1 block">Method override (optional)</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-11 rounded-md bg-surface/70 border border-edge text-white text-sm px-3" data-testid="agent-method-select">
            <option value="">Use lender default ({cfgMethod || "prepared"})</option>
            <option value="email">Email the package</option>
            <option value="portal">Portal (prepare / agent)</option>
            <option value="prepared">Just prepare package</option>
          </select>
        </div>
      </div>
      {effMethod === "email" && (
        <Input value={intake} onChange={(e) => setIntake(e.target.value)} type="email" placeholder={selected?.submission?.intake_email ? `Lender intake email (default: ${selected.submission.intake_email})` : "Lender intake email"} className="bg-surface/70 border-edge text-white h-11 mt-2" data-testid="agent-intake-email" />
      )}
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the lender…" className="bg-surface/70 border-edge text-white mt-2" data-testid="agent-note" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={submit} disabled={busy} className="btn-gold rounded-lg h-11 px-5 gap-2" data-testid="agent-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bot className="w-4 h-4" /> Submit with BullsEye Agent</>}
        </Button>
        <Button onClick={testAgent} disabled={busy} variant="outline" className="btn-outline-glass rounded-lg h-11 gap-2" data-testid="agent-test-btn">
          <Bot className="w-4 h-4" /> Test Portal Agent
        </Button>
      </div>

      {rows.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="agent-submissions-list">
          {rows.map((r) => {
            const st = SUB_STATUS[r.status] || SUB_STATUS.created;
            return (
              <div key={r.id} className="rounded-xl border border-edge bg-surface/40 p-4" data-testid={`agent-row-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-white text-sm font-medium">{r.lender_name} <span className="text-faint text-xs">· {r.method}</span></div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`agent-status-${r.id}`}>{st.label}</span>
                </div>
                <div className="text-xs text-faint mt-1">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}{r.sent_to?.length ? ` · sent to ${r.sent_to.join(", ")}` : ""}{r.confirmation_number ? ` · Conf #${r.confirmation_number}` : ""}</div>
                {r.portal_result?.detail && <div className="text-xs text-amber-300/90 mt-1">{r.portal_result.detail}</div>}
                {r.method === "email" && (r.attachments?.length > 0 || r.prepared_attachments?.length > 0) && (
                  <div className="text-xs text-emerald-300/90 mt-1" data-testid={`agent-attachments-${r.id}`}>
                    {r.attachments?.length ? "Sent as PDF download links" : "Prepared (not sent)"}: cover sheet + {(r.attachments?.length ? r.attachments : r.prepared_attachments).join(", ")}
                    {r.skipped_attachments?.length ? <span className="text-amber-300/90"> {"\u00b7"} too large, in package link: {r.skipped_attachments.join(", ")}</span> : null}
                  </div>
                )}
                {r.log?.length > 0 && <div className="text-xs text-dim mt-1" data-testid={`agent-last-log-${r.id}`}>{r.log[r.log.length - 1].event}</div>}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a href={`${API}/lender-package/${r.access_token}`} target="_blank" rel="noreferrer" data-testid={`agent-package-${r.id}`}>
                    <Button variant="outline" size="sm" className="btn-outline-glass rounded-lg h-9 gap-2 text-xs"><Download className="w-3.5 h-3.5" /> Package ZIP</Button>
                  </a>
                  {r.portal_url && <a href={r.portal_url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="btn-outline-glass rounded-lg h-9 gap-2 text-xs"><ExternalLink className="w-3.5 h-3.5" /> Open portal</Button></a>}
                  {(r.screenshots || []).map((_, i) => (
                    <a key={i} href={`${API}/admin/lender-submissions/${r.id}/screenshot/${i}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="btn-outline-glass rounded-lg h-9 gap-2 text-xs"><FileText className="w-3.5 h-3.5" /> Screenshot {i + 1}</Button></a>
                  ))}
                  {r.method === "portal" && ["manual_required", "error", "prepared"].includes(r.status) && (
                    <Button onClick={() => retry(r.id)} size="sm" variant="outline" className="btn-outline-glass rounded-lg h-9 gap-1 text-xs" data-testid={`agent-retry-${r.id}`}><Bot className="w-3.5 h-3.5" /> Retry Robot</Button>
                  )}
                  {r.method === "email" && r.intake_email && ["manual_required", "error"].includes(r.status) && (
                    <Button onClick={() => retry(r.id)} size="sm" variant="outline" className="btn-outline-glass rounded-lg h-9 gap-1 text-xs" data-testid={`agent-resend-${r.id}`}><Bot className="w-3.5 h-3.5" /> Resend Email</Button>
                  )}
                  {r.status !== "confirmed" && (
                    <div className="flex items-center gap-1">
                      <Input value={confMap[r.id] || ""} onChange={(e) => setConfMap((m) => ({ ...m, [r.id]: e.target.value }))} placeholder="Confirmation #" className="bg-surface/70 border-edge text-white h-9 w-40 text-xs" data-testid={`agent-conf-input-${r.id}`} />
                      <Button onClick={() => confirm(r.id)} size="sm" className="btn-gold rounded-lg h-9 gap-1 text-xs" data-testid={`agent-conf-btn-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Record</Button>
                    </div>
                  )}
                </div>
                <NeedsChecklist r={r} onChanged={load} />
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
